<template>
  <div
    v-if="preferredEmployees.length > 0"
    class="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4"
  >
    <!-- Collapsible Header -->
    <button
      type="button"
      @click="$emit('toggle-section')"
      class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
    >
      <ChevronDownIcon v-if="isExpanded" class="h-4 w-4" />
      <ChevronRightIcon v-else class="h-4 w-4" />
      <ChartBarIcon class="h-4 w-4 text-indigo-600" />
      Beschikbaarheid
    </button>

    <!-- Expandable Content -->
    <div v-if="isExpanded" class="space-y-4 mt-4">
      <EmployeeAvailabilityCard
        v-for="empId in preferredEmployees"
        :key="'graph-' + empId"
        :employee-id="empId"
        :employee-name="getEmployeeName(empId)"
        :employee="getEmployeeWithWeeks(empId)"
        :period-start="periodStart"
        :period-end="periodEnd"
        :potential-dpw="potentialDpw"
        :potential-label="potentialLabel"
        :exclude-proposed-preference-id="excludeProposedPreferenceId"
        :is-selected="selectedEmployeeId === empId"
        :is-expanded="isGraphExpanded(empId)"
        :badge="selectedEmployeeId === empId ? 'selected' : null"
        @toggle="$emit('toggle-graph', empId)"
      />
    </div>
  </div>
</template>

<script setup>
import { ChevronDownIcon, ChevronRightIcon, ChartBarIcon } from '@heroicons/vue/24/outline'
import EmployeeAvailabilityCard from '@/components/common/cards/EmployeeAvailabilityCard.vue'

const props = defineProps({
  prospectId: {
    type: Number,
    required: true
  },
  prefId: {
    type: [String, Number],
    required: true
  },
  preferredEmployees: {
    type: Array,
    default: () => []
  },
  employeeAvailabilityData: {
    type: Object,
    default: () => ({})
  },
  selectedEmployeeId: {
    type: String,
    default: null
  },
  expandedGraphs: {
    type: Object,
    default: () => ({})
  },
  employeesMap: {
    type: Object,
    default: () => ({})
  },
  isExpanded: {
    type: Boolean,
    default: true
  },
  periodStart: {
    type: String,
    default: null
  },
  periodEnd: {
    type: String,
    default: null
  },
  potentialDpw: {
    type: Number,
    default: 0
  },
  potentialLabel: {
    type: String,
    default: 'Dit project'
  },
  excludeProposedPreferenceId: {
    type: [Number, String],
    default: null
  }
})

defineEmits(['toggle-section', 'toggle-graph'])

/**
 * Check if an employee graph is expanded (default: true)
 * @param {string} empId
 * @returns {boolean}
 */
function isGraphExpanded(empId) {
  return props.expandedGraphs[empId] !== false
}

/**
 * Get employee with weeks data for chart
 * @param {string} empId
 * @returns {Object|null}
 */
function getEmployeeWithWeeks(empId) {
  return props.employeeAvailabilityData[empId] || null
}

/**
 * Get employee name
 * @param {string} empId
 * @returns {string}
 */
function getEmployeeName(empId) {
  return props.employeesMap[empId]?.name || empId
}
</script>
